// Master permission registry — single source of truth for frontend.
// Format: module.operation  (dot-separated, all lowercase)
// Mirror: backend/config/permissions.js — keep both in sync.

export const PERMISSIONS = [
  // ── Patients ──────────────────────────────────────────────────────────────
  { key: 'patients.view',              label: 'Patients — View all patients' },
  { key: 'patients.view_own',          label: 'Patients — View own (assigned) patients only' },
  { key: 'patients.add',               label: 'Patients — Add new patient' },
  { key: 'patients.edit',              label: 'Patients — Edit patient details' },
  { key: 'patients.delete',            label: 'Patients — Delete patient' },
  { key: 'patients.dashboard',         label: 'Patients — Open patient dashboard' },
  { key: 'patients.irregular',         label: 'Patients — View irregular patients' },
  { key: 'patients.view_outstanding',  label: 'Patients — View outstanding balance' },
  { key: 'patients.escalate',          label: 'Patients — Raise escalation' },
  { key: 'patients.view_stats',        label: 'Patients — View stats cards (counts)' },

  // ── Appointments ──────────────────────────────────────────────────────────
  { key: 'appointments.view',          label: 'Appointments — View calendar' },
  { key: 'appointments.add',           label: 'Appointments — Add appointment' },
  { key: 'appointments.edit',          label: 'Appointments — Edit appointment' },
  { key: 'appointments.delete',        label: 'Appointments — Delete appointment' },
  { key: 'appointments.complete',      label: 'Appointments — Mark as completed' },

  // ── Sessions ──────────────────────────────────────────────────────────────
  { key: 'sessions.conduct',           label: 'Sessions — Conduct session (iPad view)' },
  { key: 'sessions.monitor',           label: 'Sessions — Monitor live sessions' },

  // ── Consultation ──────────────────────────────────────────────────────────
  { key: 'consultation.view',          label: 'Consultation — View consultation records' },
  { key: 'consultation.edit',          label: 'Consultation — Edit consultation data' },
  { key: 'consultation.create_plan',   label: 'Consultation — Generate treatment plan' },
  { key: 'consultation.reset',         label: 'Consultation — Reset today consultation' },
  { key: 'consultation.mynotes',       label: 'Consultation — View/edit MyNotes' },

  // ── Billing ───────────────────────────────────────────────────────────────
  { key: 'billing.view',               label: 'Billing — View bills' },
  { key: 'billing.create',             label: 'Billing — Create new bill' },
  { key: 'billing.print',              label: 'Billing — Print bills' },
  { key: 'billing.discount',           label: 'Billing — Apply discounts' },

  // ── Payments ──────────────────────────────────────────────────────────────
  { key: 'payments.view',              label: 'Payments — View payment ledger' },
  { key: 'payments.confirm',           label: 'Payments — Confirm/reject online payments' },

  // ── Leads / CRM ───────────────────────────────────────────────────────────
  { key: 'leads.view',                 label: 'Leads — View call leads' },
  { key: 'leads.add',                  label: 'Leads — Add new leads' },
  { key: 'leads.edit',                 label: 'Leads — Edit leads' },
  { key: 'leads.delete',               label: 'Leads — Delete leads' },
  { key: 'leads.kit',                  label: 'Leads — Manage KIT campaigns' },

  // ── Flows ─────────────────────────────────────────────────────────────────
  { key: 'flows.view',                 label: 'Flows — View flow dashboard' },
  { key: 'flows.add',                  label: 'Flows — Create new flows' },
  { key: 'flows.edit',                 label: 'Flows — Edit flows' },
  { key: 'flows.delete',               label: 'Flows — Delete flows' },
  { key: 'flows.assign',               label: 'Flows — Assign flows to patients' },

  // ── Online Sessions ───────────────────────────────────────────────────────
  { key: 'online.view',                label: 'Online — View online sessions' },
  { key: 'online.add',                 label: 'Online — Create online events' },
  { key: 'online.edit',                label: 'Online — Edit online events' },
  { key: 'online.delete',              label: 'Online — Delete online events' },

  // ── Resources / Content ───────────────────────────────────────────────────
  { key: 'resources.view',             label: 'Resources — View resources' },
  { key: 'resources.add',              label: 'Resources — Add resources' },
  { key: 'resources.edit',             label: 'Resources — Edit resources' },
  { key: 'resources.delete',           label: 'Resources — Delete resources' },
  { key: 'resources.assign',           label: 'Resources — Assign videos/files to patients' },

  // ── Inventory ─────────────────────────────────────────────────────────────
  { key: 'inventory.view',             label: 'Inventory — View inventory' },
  { key: 'inventory.add',              label: 'Inventory — Add items' },
  { key: 'inventory.edit',             label: 'Inventory — Edit items' },
  { key: 'inventory.delete',           label: 'Inventory — Delete items' },
  { key: 'inventory.purchase_order',   label: 'Inventory — Purchase order operations' },

  // ── Services ──────────────────────────────────────────────────────────────
  { key: 'services.view',              label: 'Services — View service catalogue' },
  { key: 'services.add',               label: 'Services — Add services' },
  { key: 'services.edit',              label: 'Services — Edit services' },
  { key: 'services.delete',            label: 'Services — Delete services' },

  // ── Exercises ─────────────────────────────────────────────────────────────
  { key: 'exercises.view',             label: 'Exercises — View exercise library' },
  { key: 'exercises.add',              label: 'Exercises — Add exercises' },
  { key: 'exercises.edit',             label: 'Exercises — Edit exercises' },
  { key: 'exercises.delete',           label: 'Exercises — Delete exercises' },

  // ── BPConfig ─────────────────────────────────────────────────────────────
  { key: 'bpconfig.view',              label: 'BPConfig — View BP selections' },
  { key: 'bpconfig.edit',              label: 'BPConfig — Edit BP selections' },

  // ── Recommendations ───────────────────────────────────────────────────────
  { key: 'recommendations.view',       label: 'Recommendations — View' },
  { key: 'recommendations.add',        label: 'Recommendations — Add' },
  { key: 'recommendations.edit',       label: 'Recommendations — Edit' },

  // ── Treatment Mappings ────────────────────────────────────────────────────
  { key: 'mappings.view',              label: 'Mappings — View treatment mappings' },
  { key: 'mappings.edit',              label: 'Mappings — Edit treatment mappings' },

  // ── Branches ──────────────────────────────────────────────────────────────
  { key: 'branches.view',              label: 'Branches — View branches' },
  { key: 'branches.add',               label: 'Branches — Add branches' },
  { key: 'branches.edit',              label: 'Branches — Edit branches' },
  { key: 'branches.delete',            label: 'Branches — Delete branches' },

  // ── Organisations ─────────────────────────────────────────────────────────
  { key: 'orgs.view',                  label: 'Orgs — View organisations' },
  { key: 'orgs.add',                   label: 'Orgs — Add organisations' },
  { key: 'orgs.edit',                  label: 'Orgs — Edit organisations' },
  { key: 'orgs.delete',                label: 'Orgs — Delete organisations' },

  // ── Expenses ──────────────────────────────────────────────────────────────
  { key: 'expenses.view',              label: 'Expenses — View expenses' },
  { key: 'expenses.add',               label: 'Expenses — Add expenses' },
  { key: 'expenses.edit',              label: 'Expenses — Edit expenses' },
  { key: 'expenses.delete',            label: 'Expenses — Delete expenses' },
  { key: 'expenses.approve',           label: 'Expenses — Approve or reject expenses' },

  // ── Leaves ────────────────────────────────────────────────────────────────
  { key: 'leaves.view_own',            label: 'Leaves — View own leaves' },
  { key: 'leaves.apply',               label: 'Leaves — Apply for own leave' },
  { key: 'leaves.view_all',            label: 'Leaves — View all staff leaves' },
  { key: 'leaves.approve',             label: 'Leaves — Approve or reject leaves' },
  { key: 'leaves.handover',            label: 'Leaves — Create and manage handovers' },
  { key: 'leaves.revert',              label: 'Leaves — Revert handover when therapist returns' },

  // ── Attendance ────────────────────────────────────────────────────────────
  { key: 'attendance.view_own',        label: 'Attendance — View own attendance' },
  { key: 'attendance.view_all',        label: 'Attendance — View all staff attendance' },
  { key: 'attendance.edit',            label: 'Attendance — Edit attendance records' },
  { key: 'attendance.review',          label: 'Attendance — Review attendance records' },
  { key: 'attendance.policy',          label: 'Attendance — Configure attendance policies' },

  // ── Payroll ───────────────────────────────────────────────────────────────
  { key: 'payroll.view',               label: 'Payroll — View payroll records' },
  { key: 'payroll.process',            label: 'Payroll — Process payroll' },
  { key: 'payroll.edit',               label: 'Payroll — Edit payroll records' },
  { key: 'payroll.documents',          label: 'Payroll — View/upload staff documents' },

  // ── Reports / OTT Dashboard ───────────────────────────────────────────────
  { key: 'reports.view',               label: 'Reports — View OTT dashboard' },
  { key: 'reports.sessions',           label: 'Reports — View session reports' },
  { key: 'checklist.view',             label: 'Checklist — View OTT checklists' },
  { key: 'checklist.add',              label: 'Checklist — Add checklists' },
  { key: 'checklist.edit',             label: 'Checklist — Edit checklists' },
  { key: 'checklist.delete',           label: 'Checklist — Delete checklists' },

  // ── Dashboard ─────────────────────────────────────────────────────────────
  { key: 'dashboard.view_others',      label: 'Dashboard — View other users data' },

  // ── Users ─────────────────────────────────────────────────────────────────
  { key: 'users.view',                 label: 'Users — View user list' },
  { key: 'users.add',                  label: 'Users — Add new user' },
  { key: 'users.edit',                 label: 'Users — Edit user details' },
  { key: 'users.delete',              label: 'Users — Delete user' },
  { key: 'users.assign_permissions',   label: 'Users — Assign permissions to users' },

  // ── Roles Management ──────────────────────────────────────────────────────
  { key: 'roles.view',                 label: 'Roles — View roles list' },
  { key: 'roles.add',                  label: 'Roles — Add roles' },
  { key: 'roles.edit',                 label: 'Roles — Edit roles' },
  { key: 'roles.delete',               label: 'Roles — Delete roles' },

  // ── Escalation ────────────────────────────────────────────────────────────
  { key: 'escalation.acknowledge',     label: 'Escalation — Acknowledge escalations' },

  // ── Patient Files ─────────────────────────────────────────────────────────
  { key: 'patient_files.view',         label: 'Patient Files — View reports & resources' },
  { key: 'patient_files.upload',       label: 'Patient Files — Upload files' },
  { key: 'patient_files.delete',       label: 'Patient Files — Delete files' },

  // ── Admin / Data Integrity ────────────────────────────────────────────────
  { key: 'admin.scan',                 label: 'Admin — Scan for data anomalies' },
  { key: 'admin.cleanup',              label: 'Admin — Clean up orphan records' },

  // ── Misc ──────────────────────────────────────────────────────────────────
  { key: 'scanner.use',                label: 'Scanner — Use image-to-text scanner' },
  { key: 'sites.view',                 label: 'Sites — View sites' },
  { key: 'sites.add',                  label: 'Sites — Add sites' },
  { key: 'sites.edit',                 label: 'Sites — Edit sites' },
  { key: 'sites.delete',               label: 'Sites — Delete sites' },
  { key: 'notifications.view',         label: 'Notifications — View messages' },
  { key: 'app.share',                  label: 'App — Share app link' },
];

// Grouped by module — drives the permission assignment UI checkboxes
// Sorted: Main menu screens first, then Admin submenu screens (matches sidebar order)
export const PERMISSION_GROUPS = [
  // ── Main Menu Screens ───────────────────────────────────────────────────
  { group: 'Patients',        screen: 'All Saadhak',                keys: ['patients.view','patients.view_own','patients.add','patients.edit','patients.delete','patients.dashboard','patients.irregular','patients.view_outstanding','patients.escalate'] },
  { group: 'Dashboard',       screen: 'Therapist Dashboard',        keys: ['dashboard.view_others'] },
  { group: 'Sessions',        screen: 'Live Sessions',              keys: ['sessions.conduct','sessions.monitor'] },
  { group: 'Appointments',    screen: 'Appointments',               keys: ['appointments.view','appointments.add','appointments.edit','appointments.delete','appointments.complete'] },
  { group: 'Billing',         screen: 'Billing',                    keys: ['billing.view','billing.create','billing.print','billing.discount'] },
  { group: 'Reports / OTT',   screen: 'OTT Dashboard',             keys: ['reports.view','reports.sessions','checklist.view','checklist.add','checklist.edit','checklist.delete'] },
  { group: 'Leads / CRM',     screen: 'KIT',                       keys: ['leads.view','leads.add','leads.edit','leads.delete','leads.kit'] },
  { group: 'Online',          screen: 'Online',                     keys: ['online.view','online.add','online.edit','online.delete'] },
  { group: 'Flows',           screen: 'Flow Manager',               keys: ['flows.view','flows.add','flows.edit','flows.delete','flows.assign'] },
  { group: 'Resources',       screen: 'Resources',                  keys: ['resources.view','resources.add','resources.edit','resources.delete','resources.assign'] },
  { group: 'Inventory',       screen: 'Inventory',                  keys: ['inventory.view','inventory.add','inventory.edit','inventory.delete','inventory.purchase_order'] },
  { group: 'Expenses',        screen: 'Expenses',                   keys: ['expenses.view','expenses.add','expenses.edit','expenses.delete','expenses.approve'] },
  { group: 'Attendance',      screen: 'My Attendance',              keys: ['attendance.view_own','attendance.view_all','attendance.edit'] },
  { group: 'Leaves',          screen: 'Leaves Updates',             keys: ['leaves.view_own','leaves.apply','leaves.view_all','leaves.approve','leaves.handover','leaves.revert'] },
  { group: 'Notifications',   screen: 'Notifications',              keys: ['notifications.view'] },
  { group: 'Share',           screen: 'Share App',                  keys: ['app.share'] },
  // ── Admin Submenu Screens ───────────────────────────────────────────────
  { group: 'Payments',        screen: 'Payments Ledger',            keys: ['payments.view','payments.confirm'] },
  { group: 'Consultation',    screen: 'Patient Consultation',       keys: ['consultation.view','consultation.edit','consultation.create_plan','consultation.reset','consultation.mynotes'] },
  { group: 'Patient Files',   screen: 'Patient Files & Reports',    keys: ['patient_files.view','patient_files.upload','patient_files.delete'] },
  { group: 'Escalation',      screen: 'Escalations',                keys: ['escalation.acknowledge'] },
  { group: 'Services',        screen: 'Services',                   keys: ['services.view','services.add','services.edit','services.delete'] },
  { group: 'Recommendations', screen: 'Recommendations',            keys: ['recommendations.view','recommendations.add','recommendations.edit'] },
  { group: 'Mappings',        screen: 'Mappings',                   keys: ['mappings.view','mappings.edit'] },
  { group: 'Exercises',       screen: 'Exercises',                  keys: ['exercises.view','exercises.add','exercises.edit','exercises.delete'] },
  { group: 'BPConfig',        screen: 'BPConfig',                   keys: ['bpconfig.view','bpconfig.edit'] },
  { group: 'Branches',        screen: 'Branches',                   keys: ['branches.view','branches.add','branches.edit','branches.delete'] },
  { group: 'Orgs',            screen: 'Orgs',                       keys: ['orgs.view','orgs.add','orgs.edit','orgs.delete'] },
  { group: 'Attendance Policy', screen: 'Attendance Policies',      keys: ['attendance.policy'] },
  { group: 'Attendance Report', screen: 'Attendance Report',        keys: ['attendance.view_all', 'attendance.review'] },
  { group: 'Users',           screen: 'User Management',            keys: ['users.view','users.add','users.edit','users.delete','users.assign_permissions'] },
  { group: 'Roles',           screen: 'User Management (Roles)',    keys: ['roles.view','roles.add','roles.edit','roles.delete'] },
  { group: 'Payroll',         screen: 'Payroll',                    keys: ['payroll.view','payroll.process','payroll.edit','payroll.documents'] },
  { group: 'Admin',           screen: 'Scan Anomalies',             keys: ['admin.scan','admin.cleanup'] },
  { group: 'Scanner',         screen: 'Scanner',                    keys: ['scanner.use'] },
  { group: 'Sites',           screen: 'Sites',                      keys: ['sites.view','sites.add','sites.edit','sites.delete'] },
];

// Fast lookup: key → label
export const PERMISSION_MAP = Object.fromEntries(PERMISSIONS.map(p => [p.key, p.label]));

// Roles that always have full access — skip permission checks entirely
export const SUPER_ROLES = ['owner', 'solution'];
